<template>
  <InstructorLayout>
    <div class="vacation-settings">
      <h1>📅 計画休暇</h1>

      <!-- エラー/成功トースト（将来拡張用） -->
      <div v-if="toast.show" class="toast" :class="toast.type">
        {{ toast.message }}
      </div>

      <!-- 操作アクション -->
      <div class="actions">
        <button class="btn" @click="goToToday">今日に戻る</button>
        <span class="hint">💡 日付をタップ: その日を計画休暇に追加/解除</span>
      </div>

      <!-- 読み込み中スケルトン -->
      <div v-if="isLoading" class="skeleton-months">
        <div class="skeleton-month" v-for="i in 3" :key="i">
          <div class="skeleton-title"></div>
          <div class="skeleton-grid">
            <div v-for="j in 42" :key="j" class="skeleton-cell"></div>
          </div>
        </div>
      </div>

      <!-- 3か月カレンダー -->
      <div v-else class="calendar-row" aria-label="3ヶ月のカレンダー一覧">
        <CalendarMonth
          v-for="m in months"
          :key="`${m.year}-${m.month}`"
          :year="m.year"
          :month="m.month"
          :plannedMap="plannedMap"
          :busy="isSaving"
          @toggle="onToggleDate"
        />
      </div>

      <!-- 既存のフォームも残す（任意の範囲登録やメモ用） -->
      <div class="card">
        <h2>期間で登録（オプション）</h2>
        <form @submit.prevent="saveRange">
          <div class="form-row">
            <label>
              開始日（明日以降・JST基準） 
              <input type="date" v-model="form.startDate" :min="isoTomorrowJST()" required class="form-input">
            </label>
            <label>
              終了日 
              <input type="date" v-model="form.endDate" :min="form.startDate || isoTomorrowJST()" required class="form-input">
            </label>
          </div>
          <label>
            理由（任意・未入力OK）
            <input 
              type="text" 
              v-model="form.reason" 
              placeholder="任意：メモがあれば" 
              class="form-input"
              autocomplete="off"
              autocapitalize="none"
              inputmode="text"
            />
          </label>
          <button class="btn primary" :disabled="isSaving">
            {{ isSaving ? '登録中…' : 'まとめて登録' }}
          </button>
        </form>
      </div>

      <!-- 登録済み休暇一覧 -->
      <div class="card">
        <h2>登録済み休暇</h2>
        <div v-if="isLoading" class="muted">読み込み中…</div>
        <div v-else-if="absences.length === 0" class="muted">登録済みの休暇はありません</div>
        <div v-else class="list">
          <div class="row" v-for="a in absences" :key="a.id">
            <div>
              <b>計画休暇</b>：{{ formatJP(a.start_date) }} 〜 {{ formatJP(a.end_date) }}
              <span v-if="a.reason" class="reason-text">（{{ a.reason }}）</span>
              <span v-if="!a.is_active" class="tag">解除済み</span>
            </div>
            <div class="row-actions">
              <button 
                class="btn danger" 
                @click="deactivate(a.id)" 
                :disabled="isSaving || !a.is_active"
              >
                解除
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </InstructorLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { supabase } from '../../supabaseClient'
import InstructorLayout from '../../components/layout/InstructorLayout.vue'
import CalendarMonth from '../../components/calendar/CalendarMonth.vue'

// 状態管理
const isLoading = ref(false)
const isSaving = ref(false)
const absences = ref([])
const plannedMap = ref(new Map()) // Map<ISO date, {id,is_active}>
const current = ref(new Date()) // 今日基準
const currentInstructor = ref(null)

// フォーム
const form = ref({ startDate: '', endDate: '', reason: '' })

// トースト通知（将来拡張用）
const toast = ref({ show: false, message: '', type: 'info' })
let toastTimer = null

// トースト表示ヘルパー
function showToast(message, type = 'info', duration = 3000) {
  if (toastTimer) clearTimeout(toastTimer)
  toast.value = { show: true, message, type }
  toastTimer = setTimeout(() => {
    toast.value.show = false
  }, duration)
}

// 3か月分の年月を計算（JST基準）
const months = computed(() => {
  const base = jstNow()
  const list = []
  for (let i = 0; i < 3; i++) {
    const d = new Date(base.getFullYear(), base.getMonth() + i, 1)
    list.push({ year: d.getFullYear(), month: d.getMonth() + 1 })
  }
  return list
})

// 現在の講師情報を取得
const loadCurrentInstructor = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) throw new Error('認証が必要です')
    currentInstructor.value = user
    return user
  } catch (error) {
    console.error('講師情報取得エラー:', error)
    throw error
  }
}

// ローカル（JST想定）で YYYY-MM-DD を作る（UTCずれ防止）
function isoLocal(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// JSTの「今」「今日」「明日」を返す（タイムゾーン依存を排除）
function jstNow() {
  const now = new Date()
  return new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }))
}

function isoTodayJST() {
  return isoLocal(jstNow())
}

function isoTomorrowJST() {
  const j = jstNow()
  j.setDate(j.getDate() + 1)
  return isoLocal(j)
}

// 3か月範囲の計算（JST基準）
function firstDayISO() {
  const b = jstNow()
  const d = new Date(b.getFullYear(), b.getMonth(), 1)
  return isoLocal(d)
}

function lastDayISOPlus2() {
  const b = jstNow()
  const d = new Date(b.getFullYear(), b.getMonth() + 3, 0) // 再来月末日
  return isoLocal(d)
}

// 日付フォーマット
function formatJP(s) {
  if (!s) return ''
  const d = new Date(s)
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}

// 過去日判定（タップ不可用）
function isPast(iso) {
  const today = isoLocal(new Date())
  return iso < today
}

// 休暇一覧を読み込み
async function loadAbsences() {
  if (!currentInstructor.value) return
  
  isLoading.value = true
  try {
    const { data, error } = await supabase
      .from('instructor_absences')
      .select('id, instructor_id, type, is_active, start_date, end_date, reason')
      .eq('instructor_id', currentInstructor.value.id)
      .eq('type', 'planned_vacation')
      .gte('start_date', firstDayISO())
      .lte('end_date', lastDayISOPlus2())
      .order('start_date', { ascending: true })
    
    if (error) throw error
    absences.value = data || []

    // 単日で入れる運用：start=end=その日想定
    const map = new Map()
    for (const a of absences.value) {
      if (a.start_date === a.end_date) {
        map.set(a.start_date, { id: a.id, is_active: a.is_active })
      } else {
        // 万一の範囲休暇も日割り展開してMapへ
        const start = new Date(a.start_date)
        const end = new Date(a.end_date)
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          map.set(isoLocal(d), { id: a.id, is_active: a.is_active })
        }
      }
    }
    plannedMap.value = map
  } catch (error) {
    console.error('休暇一覧読み込みエラー:', error)
  } finally {
    isLoading.value = false
  }
}

// カレンダー日付タップ時の処理（既存理由を保持）
async function onToggleDate(isoDate) {
  if (!currentInstructor.value) return
  
  // 当日以前のブロック（計画休は明日以降のみ・JST基準）
  if (isoDate <= isoTodayJST()) {
    showToast('当日の休みはHELPトグルを使ってください', 'info')
    return
  }
  
  isSaving.value = true
  try {
    const existing = plannedMap.value.get(isoDate)
    if (existing && existing.is_active) {
      // 解除：同じ日付の planned_vacation を is_active=false
      const { error } = await supabase
        .from('instructor_absences')
        .update({ is_active: false })
        .eq('id', existing.id)
      
      if (error) throw error
      console.log(`🔄 休暇解除: ${isoDate}`)
    } else {
      // 既存レコードの取得（あれば理由を温存）
      const { data: existRows } = await supabase
        .from('instructor_absences')
        .select('id, reason, is_active')
        .eq('instructor_id', currentInstructor.value.id)
        .eq('type', 'planned_vacation')
        .eq('start_date', isoDate)
        .eq('end_date', isoDate)
        .limit(1)

      const keepReason = existRows?.[0]?.reason ?? null

      // 追加：単日休暇（start=end）- Upsertで重複防止、理由は既存を維持
      const { error } = await supabase
        .from('instructor_absences')
        .upsert({
          instructor_id: currentInstructor.value.id,
          type: 'planned_vacation',
          start_date: isoDate,
          end_date: isoDate,
          is_active: true,
          reason: keepReason     // ← 既存があれば維持
        }, { onConflict: 'instructor_id, type, start_date, end_date' })
      
      if (error) throw error
      console.log(`✅ 休暇追加: ${isoDate}${keepReason ? ` (理由: ${keepReason})` : ''}`)
    }
    await loadAbsences()
  } catch (error) {
    console.error('休暇トグルエラー:', error)
    showToast('休暇の設定に失敗しました', 'error')
    // alert('休暇の設定に失敗しました') // トーストに置き換え
  } finally {
    isSaving.value = false
  }
}

// 期間で一括登録
async function saveRange() {
  if (!form.value.startDate || !form.value.endDate || !currentInstructor.value) return
  
  // バリデーション：開始日が明日以降（JST基準）
  if (form.value.startDate < isoTomorrowJST()) {
    showToast('開始日は「明日」以降で指定してください（JST基準）', 'error')
    return
  }
  
  // バリデーション：終了日が開始日以降
  if (form.value.endDate < form.value.startDate) {
    showToast('終了日は開始日以降で指定してください', 'error')
    return
  }
  
  isSaving.value = true
  try {
    // 期間を1日ずつ insert
    const start = new Date(form.value.startDate)
    const end = new Date(form.value.endDate)
    const rows = []
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      rows.push({
        instructor_id: currentInstructor.value.id,
        type: 'planned_vacation',
        start_date: isoLocal(d),
        end_date: isoLocal(d),
        is_active: true,
        reason: form.value.reason || null
      })
    }
    
    if (rows.length) {
      const { error } = await supabase
        .from('instructor_absences')
        .upsert(rows, { onConflict: 'instructor_id, type, start_date, end_date' })
      if (error) throw error
    }
    
    // リセット & 再読込
    form.value = { startDate: '', endDate: '', reason: '' }
    await loadAbsences()
    showToast(`${rows.length}日分の休暇を登録しました`, 'success')
    // alert(`${rows.length}日分の休暇を登録しました`) // トーストに置き換え
  } catch (error) {
    console.error('期間登録エラー:', error)
    showToast('期間登録に失敗しました', 'error')
    // alert('期間登録に失敗しました') // トーストに置き換え
  } finally {
    isSaving.value = false
  }
}

// 休暇を解除
async function deactivate(id) {
  isSaving.value = true
  try {
    const { error } = await supabase
      .from('instructor_absences')
      .update({ is_active: false })
      .eq('id', id)
    
    if (error) throw error
    await loadAbsences()
    alert('休暇を解除しました')
  } catch (error) {
    console.error('休暇解除エラー:', error)
    alert('休暇の解除に失敗しました')
  } finally {
    isSaving.value = false
  }
}

// 今日に戻る
function goToToday() {
  current.value = new Date()
  loadAbsences()
}

// 初期化
onMounted(async () => {
  await loadCurrentInstructor()
  await loadAbsences()
})
</script>

<style scoped>
.vacation-settings {
  max-width: 1100px;
  margin: 0 auto;
  padding: 16px 16px 24px;
  display: grid;
  gap: 20px;
}

/* h1: 端末幅でなめらかに可変 + 余白と行高を最適化 */
h1 {
  color: #1f2937;
  font-size: clamp(1.25rem, 2.5vw + 0.5rem, 1.5rem); /* 20px〜24px */
  line-height: 1.25;
  margin-bottom: 0.5rem;
  font-weight: 700;
}

/* h2: モバイルで詰まりすぎないように可変 */
h2 {
  color: #374151;
  font-size: clamp(1.125rem, 1.5vw + 0.75rem, 1.25rem); /* 18px〜20px */
  line-height: 1.3;
  margin: 0 0 1rem 0;
}

.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

/* ヒント: やや小さめ・行間確保 */
.hint {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* モバイルファーストのレスポンシブグリッド */
.calendar-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

@media (min-width: 640px) {
  .calendar-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1100px) {
  .calendar-row {
    grid-template-columns: repeat(3, 1fr);
  }
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  margin-bottom: 12px;
}

@media (min-width: 720px) {
  .form-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

.form-row label {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-weight: 600;
  color: #374151;
}

form label {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  font-weight: 600;
  color: #374151;
}

.form-input {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 1rem;
  line-height: 1.25;
}

input[type="date"] {
  min-height: 44px;
}

input[type="text"] {
  min-height: 44px;
  autocomplete: off;
  autocapitalize: none;
}

.btn {
  padding: 8px 16px;
  min-height: 44px;
  border-radius: 8px;
  border: 1px solid #cbd5e1;
  background: #fff;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.primary {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff;
}

.btn.danger {
  background: #ef4444;
  border-color: #ef4444;
  color: #fff;
  font-size: 0.85rem;
  padding: 6px 12px;
}

.muted {
  color: #6b7280;
  text-align: center;
  padding: 2rem;
}

.list {
  display: grid;
  gap: 8px;
}

.row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  background: #f9fafb;
}

@media (max-width: 480px) {
  .row {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

.reason-text {
  color: #6b7280;
  font-size: 0.9rem;
  font-style: italic;
  margin-left: 8px;
}

.tag {
  margin-left: 8px;
  background: #e5e7eb;
  color: #374151;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
}

.row-actions {
  display: flex;
  gap: 8px;
}

/* スケルトンスクリーン（読み込み中） */
.skeleton-months {
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;
}

@media (min-width: 640px) {
  .skeleton-months {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1100px) {
  .skeleton-months {
    grid-template-columns: repeat(3, 1fr);
  }
}

.skeleton-month {
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 12px;
}

.skeleton-title {
  height: 16px;
  background: #eee;
  border-radius: 6px;
  margin: 0 auto 8px;
  width: 60%;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

.skeleton-cell {
  aspect-ratio: 1 / 1;
  min-height: 44px;
  background: #f2f2f2;
  border-radius: 8px;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
}

@keyframes skeleton-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

@media (max-width: 960px) {
  .vacation-settings {
    padding: 12px;
  }
}

/* トースト通知（将来拡張用） */
.toast {
  position: fixed;
  top: 80px;
  right: 20px;
  max-width: 400px;
  padding: 14px 18px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-weight: 600;
  z-index: 9999;
  animation: toast-slide-in 0.3s ease-out;
}

@keyframes toast-slide-in {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast.info {
  background: #dbeafe;
  color: #1e40af;
  border-left: 4px solid #3b82f6;
}

.toast.success {
  background: #dcfce7;
  color: #166534;
  border-left: 4px solid #059669;
}

.toast.error {
  background: #fee2e2;
  color: #991b1b;
  border-left: 4px solid #dc2626;
}

@media (max-width: 480px) {
  .toast {
    top: 60px;
    right: 10px;
    left: 10px;
    max-width: none;
  }
}
</style>
